import type { CrmTrend, CrmTrendKind } from '@morales/shared';
import { useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../api';
import { useAuth } from '../auth';
import { Empty, QueryView } from '../components/ui';
import { formatDate, formatMoney, weekdayName } from '../format';
import { useLocale } from '../i18n';
import { qk } from '../queryClient';
import { ActivityDoneButton, Activities, Contacts, KindBadge, StageBadge } from './CustomersPage';

const fill = (s: string, n: number) => s.replace('{n}', String(n));

/** Follow-ups (sales, office, owner): tasks due, birthdays near, prospects not touched. Nothing is sent from here.
 * The cards go straight into the page, two to a row on a desk (Customers → Follow-ups, decision 169). */
export function Followups({ date }: { date: string }) {
  const { t, intlLocale } = useLocale();
  const query = useQuery({ queryKey: qk.followups(date), queryFn: ({ signal }) => api.followups(date, signal) });
  const when = (n: number) => (n === 0 ? t('today') : n === 1 ? t('tomorrow') : fill(t('inDays'), n));

  return (
    <QueryView query={query}>
        {(f) => (
          <>
            <section className="card stack-sm">
              <h2>{t('tasksDue')}</h2>
              {f.tasks.length === 0 ? (
                <Empty>{t('noResults')}</Empty>
              ) : (
                <ul className="list" data-testid="tasks">
                  {f.tasks.map((a) => (
                    <li key={a.id} className="row">
                      <span className="row-main">
                        <Link to={`/customers/${a.customerId}`} className="row-title">
                          {a.customerName}
                        </Link>
                        <span className="row-sub">
                          <KindBadge kind={a.kind} /> <span>{a.body}</span>
                          {a.dueOn ? <span> · {formatDate(a.dueOn, intlLocale, 'day')}</span> : null}
                        </span>
                      </span>
                      <ActivityDoneButton activity={a} customerId={a.customerId} date={date} />
                    </li>
                  ))}
                </ul>
              )}
            </section>

            <section className="card stack-sm">
              <h2>{t('birthdaysNear')}</h2>
              {f.birthdays.length === 0 ? (
                <Empty>{t('noResults')}</Empty>
              ) : (
                <ul className="list" data-testid="birthdays">
                  {f.birthdays.map((b) => (
                    <li key={b.contactId}>
                      <Link to={`/customers/${b.customerId}`} className="row">
                        <span className="row-main">
                          <span className="row-title">{b.contactName ?? '—'}</span>
                          <span className="row-sub">{b.customerName}</span>
                        </span>
                        <span className="row-end">
                          <span className="row-end-value">{when(b.inDays)}</span>
                          <span className="row-end-label">{formatDate(b.birthday, intlLocale, 'monthDay')}</span>
                        </span>
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </section>

            {/* gone quiet lives in How they are ordering, from the invoices (decision 141) */}
            <section className="card stack-sm">
              <h2>{t('prospectsStale')}</h2>
              <p className="muted">{t('prospectsHint')}</p>
              {f.prospects.length === 0 ? (
                <Empty>{t('noResults')}</Empty>
              ) : (
                <ul className="list" data-testid="prospects">
                  {f.prospects.map((p) => (
                    <li key={p.customerId}>
                      <Link to={`/customers/${p.customerId}`} className="row">
                        <span className="row-main">
                          <span className="row-title">{p.customerName}</span>
                          <span className="row-sub">
                            <StageBadge stage={p.stage} />
                          </span>
                        </span>
                        <span className="row-end">
                          <span className="row-end-value">{p.daysSince === null ? t('neverTouched') : fill(t('daysAgo'), p.daysSince)}</span>
                        </span>
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          </>
        )}
    </QueryView>
  );
}

const TRENDS: CrmTrendKind[] = ['quiet', 'less', 'new', 'more', 'steady'];
type SortKey = 'trend' | 'name' | 'last4' | 'prior4' | 'change' | 'last';

/** P2: every customer's trend from the invoices, sortable and filtered by trend; a picked customer opens its notes
 * and contacts (birthday, what they like) right here. */
export function Trends({ date }: { date: string }) {
  const { t, intlLocale } = useLocale();
  const { me } = useAuth();
  const sells = me?.role === 'owner' || me?.role === 'office' || me?.role === 'sales';
  const query = useQuery({ queryKey: qk.crmTrends(date), queryFn: ({ signal }) => api.crmTrends(date, signal) });
  const [only, setOnly] = useState<CrmTrendKind | null>(null);
  const [sort, setSort] = useState<{ key: SortKey; dir: 1 | -1 }>({ key: 'trend', dir: 1 });
  const [picked, setPicked] = useState<string | null>(null);
  const rows = useMemo(() => {
    const all = (query.data?.rows ?? []).filter((r) => !only || r.trend === only);
    const rank = (r: CrmTrend) => TRENDS.indexOf(r.trend);
    const by: Record<SortKey, (a: CrmTrend, b: CrmTrend) => number> = {
      trend: (a, b) => rank(a) - rank(b),
      name: (a, b) => a.customerName.localeCompare(b.customerName, intlLocale),
      last4: (a, b) => a.last4 - b.last4,
      prior4: (a, b) => a.prior4 - b.prior4,
      change: (a, b) => (a.changePct ?? Infinity) - (b.changePct ?? Infinity),
      last: (a, b) => (a.daysSince ?? Infinity) - (b.daysSince ?? Infinity),
    };
    // the server's order (quiet, then the biggest drop) is the trend sort; others sort the column
    return sort.key === 'trend' && sort.dir === 1 ? all : [...all].sort((a, b) => by[sort.key](a, b) * sort.dir);
  }, [query.data, only, sort, intlLocale]);
  const counts = useMemo(() => {
    const m = new Map<CrmTrendKind, number>();
    for (const r of query.data?.rows ?? []) m.set(r.trend, (m.get(r.trend) ?? 0) + 1);
    return m;
  }, [query.data]);
  const head = (key: SortKey, label: string, num = false) => (
    <th scope="col" className={num ? 'num' : undefined} aria-sort={sort.key === key ? (sort.dir === 1 ? 'ascending' : 'descending') : 'none'}>
      <button type="button" className="th-sort" onClick={() => setSort((s) => ({ key, dir: s.key === key ? (s.dir === 1 ? -1 : 1) : 1 }))}>
        {label} {sort.key === key ? (sort.dir === 1 ? '▲' : '▼') : ''}
      </button>
    </th>
  );
  const money = (v: number) => formatMoney(v, intlLocale);
  const pickedRow = (query.data?.rows ?? []).find((r) => r.customerId === picked) ?? null;

  return (
    <section className="card wide stack-sm" data-testid="trends">
      <h2>{t('trendsTitle')}</h2>
      <p className="muted">{t('trendsHint')}</p>
      <div className="chips" role="group" aria-label={t('colTrend')}>
        <button type="button" className="chip" aria-pressed={only === null} onClick={() => setOnly(null)}>
          {t('stateAll')} {query.data ? `(${query.data.rows.length})` : ''}
        </button>
        {TRENDS.map((k) => (
          <button key={k} type="button" className="chip" aria-pressed={only === k} onClick={() => setOnly(only === k ? null : k)}>
            {t(`trend_${k}`)} ({counts.get(k) ?? 0})
          </button>
        ))}
      </div>
      <QueryView query={query}>
        {() =>
          rows.length === 0 ? (
            <Empty>{t('noTrends')}</Empty>
          ) : (
            <div className="table-scroll">
              <table className="data-table trends-table">
                <thead>
                  <tr>
                    {head('name', t('customer'))}
                    {head('trend', t('colTrend'))}
                    {head('last4', t('last4Weeks'), true)}
                    {head('prior4', t('colPrior4'), true)}
                    {head('change', t('changeCol'), true)}
                    {head('last', t('lastOrder'))}
                    <th scope="col">{t('colUsualDays')}</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.customerId} className={picked === r.customerId ? 'selected' : undefined}>
                      <td>
                        <button type="button" className="link-button" aria-expanded={picked === r.customerId} onClick={() => setPicked(picked === r.customerId ? null : r.customerId)}>
                          {r.customerName}
                        </button>
                      </td>
                      <td>
                        <span className={`trend-badge trend-${r.trend}`}>{t(`trend_${r.trend}`)}</span>
                      </td>
                      <td className="num">{money(r.last4)}</td>
                      <td className="num">{money(r.prior4)}</td>
                      <td className={`num ${r.changePct !== null && r.changePct < 0 ? 'trend-down' : r.changePct !== null && r.changePct > 0 ? 'trend-up' : ''}`}>
                        {r.changePct === null ? '—' : `${r.changePct > 0 ? '+' : ''}${Math.round(r.changePct)}%`}
                      </td>
                      <td>
                        {r.lastOrderDate ? formatDate(r.lastOrderDate, intlLocale, 'monthDay') : '—'}
                        {r.daysSince !== null ? <span className="muted"> · {fill(t('daysAgo'), r.daysSince)}</span> : null}
                      </td>
                      <td>
                        {[r.usualDays.map((d) => weekdayName(d, intlLocale)).join(', '), r.usualGapDays ? (r.usualGapDays === 1 ? t('trendEveryDay') : fill(t('trendEvery'), r.usualGapDays)) : ''].filter(Boolean).join(' · ') || '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        }
      </QueryView>
      {pickedRow ? <PickedCustomer row={pickedRow} canWrite={sells} onClose={() => setPicked(null)} /> : null}
    </section>
  );
}

/** The picked customer's history and contacts, to add a note, a birthday or what they like without leaving the CRM. */
function PickedCustomer({ row, canWrite, onClose }: { row: CrmTrend; canWrite: boolean; onClose: () => void }) {
  const { t } = useLocale();
  const customer = useQuery({ queryKey: qk.customer(row.customerId), queryFn: ({ signal }) => api.customer(row.customerId, signal) });
  return (
    <div className="crm-picked stack-sm" data-testid="crm-picked">
      <div className="crm-picked-head">
        <h3>{row.customerName}</h3>
        <Link to={`/customers/${row.customerId}`}>{t('openAccount')}</Link>
        <button type="button" className="btn btn-ghost" onClick={onClose}>
          {t('close')}
        </button>
      </div>
      <div className="crm-picked-grid">
        <Activities customerId={row.customerId} canWrite={canWrite} />
        <section className="card stack-sm">
          <h2>{t('contacts')}</h2>
          <QueryView query={customer}>{(c) => <Contacts customer={c} canWrite={canWrite} />}</QueryView>
        </section>
      </div>
    </div>
  );
}
